<template>
  <div class="user-avatar-wrapper" @mouseenter="onHover = true" @mouseleave="onHover = false">
    <div class="user-avatar" :style="{ backgroundColor: avatarGradient }">
      <span class="avatar-initials">{{ initials }}</span>
      <span class="status-indicator" :class="statusClass"></span>
    </div>
    
    <transition name="fade-slide">
      <div v-if="onHover && showTooltip" class="avatar-tooltip">
        <div class="tooltip-name">{{ displayName }}</div>
        <div class="tooltip-role">{{ role }}</div>
        <div v-if="passwordExpirationDay !== null" class="tooltip-expiry" :class="expiryClass">
          <i :class="expiryIcon"></i>
          Пароль истекает через {{ passwordExpirationDay }} дн.
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';

const store = useStore();
const onHover = ref(false);

const info = computed(() => store.getters.info);
const passwordIsOld = computed(() => store.getters.info?.passwordIsOld || false);

const displayName = computed(() => {
  const u = info.value;
  if (!u) return '';
  const parts = [u.user_struct_name, u.login].filter(Boolean);
  return parts[0] || u.login || 'Пользователь';
});

const role = computed(() => info.value?.userRoleName || '');

const passwordExpirationDay = computed(() => {
  if (!info.value?.passwordExpirationDate) return null;
  const now = new Date();
  const exp = new Date(info.value.passwordExpirationDate);
  const diff = Math.abs(exp - now);
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
});

const initials = computed(() => {
  const login = info.value?.login || '';
  if (!login) return '?';
  
  // Пытаемся взять инициалы из user_struct_name (ФИО)
  // Проверяем, что user_struct_name существует и не пустой
  const fullName = info.value?.user_struct_name;
  const name = fullName && fullName.trim() ? fullName.trim() : login;
  const nameParts = name.split(/\s+/).filter(p => p.length > 0);
  
  if (nameParts.length >= 2) {
    // Формат: "Симатов Р.Л." или "Симатов Роман Леонидович"
    const surname = nameParts[0];
    // Берём все оставшиеся части (имя + отчество или инициалы)
    // Для формата "Р.Л." берём букву ПОСЛЕ точки, для полного имени — первую букву
    const nameInitials = nameParts.slice(1).map(p => {
      // Если есть точка — берём букву после неё (для "Р.Л." → "Р" и "Л")
      const dotIndex = p.indexOf('.');
      if (dotIndex >= 0 && dotIndex < p.length - 1) {
        return p[dotIndex + 1];
      }
      // Иначе первую букву (для полного имени)
      return p[0];
    }).join('');
    return (surname[0] + nameInitials).toUpperCase();
    // "Симатов Р.Л." → "СРЛ"
    // "Симатов Роман Леонидович" → "СРЛ"
  }
  
  // Если только одно слово — первые 2 буквы
  return name.substring(0, 2).toUpperCase();
});

const avatarGradient = computed(() => {
  // Генерируем стабильный цвет на основе login
  const login = info.value?.login || 'user';
  let hash = 0;
  for (let i = 0; i < login.length; i++) {
    hash = login.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const h1 = hash % 360;
  const h2 = (hash * 7) % 360;
  return `linear-gradient(135deg, hsl(${h1}, 65%, 55%), hsl(${h2}, 70%, 45%))`;
});

const statusClass = computed(() => {
  if (passwordIsOld.value) return 'status-warning';
  const days = passwordExpirationDay.value;
  if (days !== null && days < 7) return 'status-critical';
  return 'status-online';
});

const expiryClass = computed(() => {
  const days = passwordExpirationDay.value;
  if (days === null) return '';
  if (days < 7) return 'expiry-danger';
  if (days < 20) return 'expiry-warning';
  return 'expiry-ok';
});

const expiryIcon = computed(() => {
  const days = passwordExpirationDay.value;
  if (days === null) return 'pi pi-info-circle';
  if (days < 7) return 'pi pi-exclamation-triangle';
  if (days < 20) return 'pi pi-clock';
  return 'pi pi-check-circle';
});

const showTooltip = computed(() => info.value !== null && info.value !== undefined);

function onHoverChange(value) {
  onHover.value = value;
}

defineExpose({ onHover });
</script>

<style lang="scss" scoped>
.user-avatar-wrapper {
  position: relative;
  display: inline-block;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: relative;
  
  &:hover {
    transform: scale(1.08);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }
}

.avatar-initials {
  color: white;
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  user-select: none;
}

.status-indicator {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;
  transition: all 0.3s ease;
  
  &.status-online {
    background: #22c55e;
    box-shadow: 0 0 6px rgba(34, 197, 94, 0.5);
  }
  
  &.status-warning {
    background: #f59e0b;
    box-shadow: 0 0 6px rgba(245, 158, 11, 0.5);
    animation: pulse-warning 2s infinite;
  }
  
  &.status-critical {
    background: #ef4444;
    box-shadow: 0 0 6px rgba(239, 68, 68, 0.5);
    animation: pulse-critical 1.5s infinite;
  }
}

@keyframes pulse-warning {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.8; }
}

@keyframes pulse-critical {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.3); opacity: 0.6; }
}

.avatar-tooltip {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  min-width: 200px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid var(--surface-border);
  }
}

.tooltip-name {
  font-weight: 600;
  color: var(--text-color);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.tooltip-role {
  color: var(--text-color-secondary);
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
}

.tooltip-expiry {
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  border-radius: 4px;
  
  i {
    font-size: 0.85rem;
  }
  
  &.expiry-ok {
    color: #16a34a;
    background: rgba(34, 197, 94, 0.1);
  }
  
  &.expiry-warning {
    color: #d97706;
    background: rgba(245, 158, 11, 0.1);
  }
  
  &.expiry-danger {
    color: #dc2626;
    background: rgba(239, 68, 68, 0.1);
    font-weight: 600;
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.2s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px);
}
</style>
